import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import technicalMaintenanceApi from '../../api/technicalMaintenanceApi';
import { daysUntilDate } from './technicalMaintenanceHelpers';

const MaintenanceAlertsPanel = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState(null);
  const [equipment, setEquipment] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [scheduleRes, equipmentRes] = await Promise.all([
          technicalMaintenanceApi.getMaintenanceSchedule({ days_ahead: 30 }),
          technicalMaintenanceApi.getEquipment({ is_active: 'true' }),
        ]);
        setSchedule(scheduleRes?.data || scheduleRes);
        setEquipment(equipmentRes?.data || equipmentRes || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const warrantyAlerts = useMemo(() => (
    equipment
      .filter((item) => item.warranty_expiry_date)
      .map((item) => ({
        ...item,
        days_until: daysUntilDate(item.warranty_expiry_date),
      }))
      .filter((item) => item.days_until != null && item.days_until <= 90)
      .sort((a, b) => a.days_until - b.days_until)
      .slice(0, 5)
  ), [equipment]);

  const cards = [
    {
      label: 'Geciken Bakım',
      value: schedule?.overdue_count ?? 0,
      accent: '#DC2626',
      path: '/internal-audit/technical-maintenance/schedule',
      hint: 'Planlanan tarihi geçmiş',
    },
    {
      label: '30 Gün İçinde',
      value: schedule?.upcoming_count ?? 0,
      accent: '#D97706',
      path: '/internal-audit/technical-maintenance/schedule',
      hint: 'Yaklaşan bakımlar',
    },
    {
      label: 'Garanti Bitiyor',
      value: warrantyAlerts.length,
      accent: '#7C3AED',
      path: '/internal-audit/technical-maintenance/reports',
      hint: '90 gün içinde',
    },
  ];

  if (loading) {
    return (
      <div className="tm-kpi-grid">
        {[1, 2, 3].map((i) => <div key={i} className="tm-skeleton" style={{ height: 88 }} />)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="tm-kpi-grid">
        {cards.map((card) => (
          <button
            key={card.label}
            type="button"
            className="tm-kpi-card text-left"
            style={{ '--tm-accent': card.accent }}
            onClick={() => navigate(card.path)}
          >
            <span className="tm-kpi-label">{card.label}</span>
            <div className="tm-kpi-value">{card.value}</div>
            <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">{card.hint}</span>
          </button>
        ))}
      </div>

      {(schedule?.overdue?.length > 0 || warrantyAlerts.length > 0) && (
        <div className="tm-dash-card">
          <h3>Dikkat Gerektirenler</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {schedule?.overdue?.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-red-600 mb-2">Geciken</p>
                <ul className="space-y-2">
                  {schedule.overdue.slice(0, 4).map((row) => (
                    <li key={row.id}>
                      <button
                        type="button"
                        className="w-full text-left rounded-lg border border-red-100 dark:border-red-900/40 px-3 py-2 hover:bg-red-50 dark:hover:bg-red-950/20"
                        onClick={() => navigate(`/internal-audit/technical-maintenance/equipment/${row.equipment}`)}
                      >
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{row.equipment_name}</div>
                        <div className="text-xs text-gray-500">{row.equipment_area} · {Math.abs(row.days_until)} gün gecikmiş</div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {warrantyAlerts.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-violet-600 mb-2">Garanti</p>
                <ul className="space-y-2">
                  {warrantyAlerts.map((row) => (
                    <li key={row.id}>
                      <button
                        type="button"
                        className="w-full text-left rounded-lg border border-violet-100 dark:border-violet-900/40 px-3 py-2 hover:bg-violet-50 dark:hover:bg-violet-950/20"
                        onClick={() => navigate(`/internal-audit/technical-maintenance/equipment/${row.id}`)}
                      >
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{row.name}</div>
                        <div className="text-xs text-gray-500">
                          {row.area || '—'} · {row.days_until < 0 ? `${Math.abs(row.days_until)} gün geçmiş` : `${row.days_until} gün kaldı`}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenanceAlertsPanel;
